import PropTypes from "prop-types"
import React, { useEffect, useMemo, useState } from "react"
import MetaTags from "react-meta-tags"
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  Container,
  Row,
  Table,
  Badge,
} from "reactstrap"
import SweetAlert from "react-bootstrap-sweetalert"
import { withTranslation } from "react-i18next"
import { useParams } from "react-router-dom"

import Breadcrumbs from "components/Common/Breadcrumb"
import LoadingSpinner from "components/Common/LoadingSpinner"
import { del, get } from "helpers/api_helper"
import Pagination from "pages/Master-Data/Component/Student/Pagination"
import AddTeacher from "./AddTeacher"

const getResponseData = response => {
  if (Array.isArray(response?.data)) return response.data
  if (response?.data) return [response.data]
  if (Array.isArray(response)) return response
  return []
}

const ClassManagementDetail = props => {
  const { id } = useParams()
  const [detailData, setDetailData] = useState([])
  const [search, setSearch] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [dataPerPage, setDataPerPage] = useState(10)
  const [modalAddTeacher, setModalAddTeacher] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [errorAlert, setErrorAlert] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [teacherToDelete, setTeacherToDelete] = useState(null)
  const [successAlert, setSuccessAlert] = useState(false)

  const getClassDetail = async () => {
    setIsLoading(true)

    try {
      const response = await get(`/homeroom-class/detail/${id}`)
      setDetailData(getResponseData(response).filter(Boolean))
    } catch (error) {
      setDetailData([])
      setErrorMessage("Failed to get class detail")
      setErrorAlert(true)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    getClassDetail()
  }, [id])

  const handleDelete = async item => {
    setTeacherToDelete(null)
    setIsLoading(true)

    try {
      await del(`/homeroom-class/remove-teacher/${item.id}`)
      await getClassDetail()
      setSuccessAlert(true)
    } catch (error) {
      setErrorMessage(
        error?.response?.data?.message ||
          error?.response?.data?.error?.message ||
          "Failed to remove teacher"
      )
      setErrorAlert(true)
    } finally {
      setIsLoading(false)
    }
  }

  const filteredDetailData = useMemo(() => {
    const keyword = search.trim().toLowerCase()

    if (!keyword) return detailData

    return detailData.filter(item =>
      [item?.class?.name, item?.teacher?.full_name]
        .join(" ")
        .toLowerCase()
        .includes(keyword)
    )
  }, [detailData, search])

  useEffect(() => {
    setCurrentPage(1)
  }, [search, dataPerPage])

  const indexOfLastData = currentPage * dataPerPage
  const indexOfFirstData = indexOfLastData - dataPerPage
  const currentData = filteredDetailData.slice(
    indexOfFirstData,
    indexOfLastData
  )
  const className = detailData[0]?.class?.name || "Class Detail"

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>{className} | Class Management</title>
        </MetaTags>
        <Container fluid>
          <Breadcrumbs
            title={props.t("Class Detail")}
            breadcrumbItem={props.t("Class Management")}
          />

          <Row>
            <Col className="col-12">
              <Card>
                <CardBody>
                  <CardTitle className="h4">
                    List Teachers for Class : [{className}]
                  </CardTitle>

                  <Row className="my-2">
                    <Col md="6">
                      <div className="search-box me-2 mb-3 d-flex justify-content-start">
                        <Button
                          color="primary"
                          onClick={() => setModalAddTeacher(true)}
                          className="btn-sm m-1"
                        >
                          <i className="bx bx-plus me-1" />
                          Add Teacher
                        </Button>
                      </div>
                    </Col>
                    <Col md="6" className="mt-2">
                      <div className="search-box me-2 mb-3 d-flex justify-content-end">
                        <div className="position-relative">
                          <input
                            className="form-control"
                            type="text"
                            placeholder="Search"
                            value={search}
                            onChange={event => setSearch(event.target.value)}
                          />
                          <i className="bx bx-search-alt search-icon" />
                        </div>
                      </div>
                    </Col>
                  </Row>

                  <div className="table-responsive">
                    <Table className="table">
                      <thead>
                        <tr>
                          <th>No</th>
                          <th>Teacher Name</th>
                          <th>Email</th>
                          <th>Is Main</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {isLoading ? (
                          <tr>
                            <td colSpan="4">
                              <div className="d-flex flex-column align-items-center justify-content-center py-4">
                                <LoadingSpinner />
                                <div className="mt-2">
                                  Fetching data, please wait...
                                </div>
                              </div>
                            </td>
                          </tr>
                        ) : currentData.length > 0 ? (
                          currentData.map((item, index) => (
                            <tr key={item.id || indexOfFirstData + index}>
                              <td>{indexOfFirstData + index + 1}</td>
                              <td>{item?.teacher?.full_name || "-"}</td>
                              <td>{item?.teacher?.email || "-"}</td>
                              <td>
                                <Badge
                                  className={
                                    (item?.is_main) 
                                    ? "bg-info py-1" : "bg-secondary py-1"
                                  }
                                >
                                  {item?.is_main ? "Yes" : "No"}
                                </Badge>
                              </td>
                              <td>
                                <i
                                  className={`bx bx-trash ${item.is_main ? "text-dark" : "text-danger"}`}
                                  style={{
                                    fontSize: "24px",
                                    cursor: item.is_main ? "not-allowed" : "pointer",
                                    opacity: item.is_main ? 0.5 : 1,
                                  }}
                                  onClick={() => {
                                    if (item.is_main) return;
                                    setTeacherToDelete(item);
                                  }}
                                />
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4" className="text-center">
                              No Data
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>

                  <Pagination
                    setDataPerPage={setDataPerPage}
                    dataPerPage={dataPerPage}
                    totalData={filteredDetailData.length}
                    paginate={setCurrentPage}
                    currentPage={currentPage}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>

        <AddTeacher
          classId={id}
          modalShow={modalAddTeacher}
          toggleModal={() => setModalAddTeacher(false)}
          onSuccess={getClassDetail}
        />

        {teacherToDelete ? (
          <SweetAlert
            warning
            showCancel
            title="Are you sure?"
            confirmBtnText="Yes"
            cancelBtnText="Cancel"
            confirmBtnBsStyle="primary"
            cancelBtnBsStyle="secondary"
            onConfirm={() => handleDelete(teacherToDelete)}
            onCancel={() => setTeacherToDelete(null)}
          >
            Remove {teacherToDelete?.teacher?.full_name || "this teacher"} from
            this class?
          </SweetAlert>
        ) : null}

        {successAlert ? (
          <SweetAlert
            success
            title="Success"
            confirmBtnText="Yes"
            confirmBtnBsStyle="primary"
            onConfirm={() => setSuccessAlert(false)}
          >
            Teacher removed successfully
          </SweetAlert>
        ) : null}

        {errorAlert ? (
          <SweetAlert
            title="Oppss!"
            confirmBtnText="Yes"
            error
            confirmBtnBsStyle="primary"
            onConfirm={() => setErrorAlert(false)}
          >
            {errorMessage || "Something wrong, please try again"}
          </SweetAlert>
        ) : null}
      </div>
    </React.Fragment>
  )
}

ClassManagementDetail.propTypes = {
  t: PropTypes.any,
}

export default withTranslation()(ClassManagementDetail)
